/**
 * The address list an announcement carries — **one owner** for both readers
 * (client card `t_9cdad513`; the hub side that publishes it is `t_c61fadb5`).
 *
 * A hub can be reachable at more than one address of its own (a second NIC, a
 * routed VLAN, an interface it also announces on), and until the hub-side card
 * the advert published exactly **one** — the TXT `localAddress` pair. Measured on
 * this fleet: Rožna S1 announced `localAddress=192.168.1.132` while its A records
 * led with `192.168.1.6`, so the two readings of one record already disagreed and
 * a client that took the wrong one had no second address to try. The hub now
 * publishes the whole ranking as a second TXT pair,
 *
 *     localAddress=192.168.1.2
 *     addresses=192.168.1.2,192.168.1.72,172.29.64.1,172.18.64.1,192.168.100.17
 *
 * (`docs/plans/evidence/hub-mdns-advert-address-list.md` in the hub tree), and
 * this module is how a client reads it — the candidate list, in the order the
 * record itself ranks it:
 *
 *   1. the TXT `addresses` entries, as they arrive;
 *   2. `localAddress`, when the list above does not already carry it;
 *   3. the A records, in the order the browse answered with them.
 *
 * Deduplicated, order kept, **IPv4 only**: a dotted quad is the one family the
 * advert carries and the one the app's WebView may be handed (card `t_6b29e7d6` —
 * every other spelling of a host died as `net::ERR_NAME_NOT_RESOLVED`).
 *
 * Two readers share this file on purpose. It lives in the electron compilation
 * unit (`tsconfig.electron.json` has its own `rootDir`, so the main process
 * cannot import from `src/`), and the renderer imports it from here — one
 * implementation, so the desktop app and the phone cannot drift the first time
 * one of them learns a new shape. Nothing here touches Electron, `os` or the
 * network: it is a pure reading of a record, and its test is a table of records.
 *
 * `undefined`, `null`, an empty string or a value that is not a string at all is
 * "this record says nothing about an address" — never a claim about a hub, and
 * never an address.
 */

/** The IPv4 dotted-quad form, and only that: an A record, or a TXT entry. */
const IPV4_LITERAL = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

/**
 * Is this an address a client may be handed? The same rule the app has always
 * applied to a discovered host (card `t_6b29e7d6`), now shared instead of
 * restated per reader.
 */
export function isAddressLiteral(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = IPV4_LITERAL.exec(value.trim());
  if (!match) return false;
  return match.slice(1).every((part) => Number(part) <= 255);
}

/**
 * Every address one field of a record spells, in the order it spells them.
 *
 * Three shapes arrive on this wire, all measured:
 *
 *   - a string — the advert's `addresses` is `"<ip>,<ip>,…"` (comma separated,
 *     one TXT item, capped at 255 bytes by the hub), and a client that split it
 *     wrong would dial a value with a comma inside;
 *   - an array of strings — the A records, exactly as `bonjour-service` /
 *     `capacitor-zeroconf` answer them;
 *   - an array of objects — `/api/info`'s own shape,
 *     `[{ address: '192.168.1.2', iface: 'Ethernet' }, …]`, which the loopback
 *     reader intersects with this device's interfaces.
 *
 * Whitespace and separators are the only things tolerated: anything that is not
 * an IPv4 literal is dropped by the caller, never repaired here.
 */
export function addressValues(value: unknown): string[] {
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  }
  if (Array.isArray(value)) {
    const out: string[] = [];
    for (const entry of value) {
      const address = typeof entry === 'string' ? entry : (entry as { address?: unknown } | null | undefined)?.address;
      if (typeof address === 'string' && address.trim()) out.push(address.trim());
    }
    return out;
  }
  return [];
}

/** The three fields of an announcement that can name an address. */
export interface AnnounceAddressRecord {
  /**
   * The TXT `addresses` pair: the hub's whole ranking, best first, its own
   * `localAddress` at the head. Absent on every hub that predates the hub-side
   * card — an absent key is the normal case there, and this module must never
   * require it.
   */
  addresses?: unknown;
  /** The TXT `localAddress` pair: the single address the old advert carried. */
  localAddress?: unknown;
  /** The A records the browse answered with, in the order it answered them. */
  hostAddresses?: unknown;
}

/**
 * The addresses a client should dial, in the order it should dial them.
 *
 * Empty means the record announced **no** address this client may use — the one
 * and only condition behind the verdict "this hub announced no IP address on
 * this network". A record with an `addresses` key whose every entry is unusable
 * (a name, a v6 literal, an empty entry) lands there too, on purpose: the hub
 * said something, and what it said is not an address.
 */
export function announcedAddresses(record: AnnounceAddressRecord): string[] {
  const ordered: string[] = [];
  const push = (value: unknown): void => {
    for (const address of addressValues(value)) {
      if (!isAddressLiteral(address)) continue;
      if (ordered.includes(address)) continue;
      ordered.push(address);
    }
  };

  // 1. the hub's own ranking, 2. the head of it as a separate key (an old-style
  // record is *only* this), 3. the addresses mDNS resolved for the host.
  push(record.addresses);
  push(record.localAddress);
  push(record.hostAddresses);

  return ordered;
}

/**
 * How long an address **behind** a record's own one gets to answer (card
 * `t_9cdad513`).
 *
 * The record's first address is dialled with the caller's own budget — exactly
 * the behaviour of a single-address record. Every address behind it gets this
 * one, for the reason `probeSubnet` measures its probes against: a hub on this
 * device's own networks answers `/api/info` in single-digit milliseconds, so
 * this only ever cuts an address that was never going to answer. It is what
 * keeps a walk down a five-address advert (this fleet's dev hub publishes five,
 * two of them bridges nothing can route to) from paying the full budget per
 * address, on a phone, inside the scan window.
 */
export const HUB_ADDRESS_PROBE_MS = 700;

/**
 * The budget for the *n*-th address of a walk, counted from 0: the caller's own
 * budget for the record's own address, the short one for everything behind it
 * (`HUB_ADDRESS_PROBE_MS`). One function, so the desktop walk and the phone's
 * identity question cannot price the same list differently.
 */
export function addressProbeBudget(index: number, timeoutMs?: number): number | undefined {
  if (index <= 0) return timeoutMs;
  return Math.min(timeoutMs ?? HUB_ADDRESS_PROBE_MS, HUB_ADDRESS_PROBE_MS);
}

/**
 * The list to walk for a record whose address was already chosen elsewhere — the
 * record's own address first, then the advert's ranking behind it, deduplicated.
 *
 * The order above is the ranking a **fresh** record carries; this is for a
 * caller that holds a record already folded together (a re-announcement that
 * carries no `addresses` at all, a merged cache entry): the address the record
 * is known by leads — it is the one its card prints — and the rest of the
 * advert's list rides behind it, so nothing that was published is lost.
 */
export function dialableAddresses(known: string | undefined | null, announced: unknown): string[] {
  const ordered: string[] = [];
  if (isAddressLiteral(known)) ordered.push(known.trim());
  for (const address of announcedAddresses({ addresses: announced })) {
    if (!ordered.includes(address)) ordered.push(address);
  }
  return ordered;
}
